import React, { useCallback, useContext, useEffect, useState } from 'react';

import { ILayoutProps, LayoutContext } from '@/components/Layout';
import { AsaphButton } from '@/components/shared/Button';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import {
	convertFilterEnumToSongKey,
	FilterEnum,
	SearchAndFilterProps,
} from '@/components/shared/SearchAndFilter';
import { SongCard } from '@/components/shared/SongCard';
import { SonglistPanelLayout } from '@/components/SonglistPanelLayout';
import {
	INITIAL_SONGLIST_RESULT_SIZE,
	UserMilestones,
} from '@/config/constants';
import {
	Song,
	TagsByCategoryFilter,
	useAddSongsToMyTeamMutation,
	useSearchSongsMutation,
} from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { CircularProgress } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import styles from './AddSongs.module.scss';

export type AddSongsConfig = {
	onlySongIds?: string[];
	excludeMyTeamSongs?: boolean;
	hideActionButton?: boolean;
	hideSelection?: boolean;
};

export interface AddSongsProps {
	onClose: (addedSongIds: string[]) => void;
	onCustomSongOpen: () => void;
	openSong: ILayoutProps['openSong'];
	config?: AddSongsConfig;
}

type SongsList = {
	searchTerm: string;
	results: Song[];
	searchTagsStringified: string;
	totalCount: number;
};

const INITIAL_SONGS_LIST: SongsList = {
	searchTerm: '',
	searchTagsStringified: '',
	results: [],
	totalCount: 0,
};

export const AddSongs: React.FC<AddSongsProps> = ({
	onClose,
	onCustomSongOpen,
	openSong,
	config,
}) => {
	const {
		mutateAsync: searchSongs,
		isLoading: searchSongsLoading,
		error,
	} = useSearchSongsMutation();
	const { mutateAsync: addSongsToMyTeam, isLoading: submitting } =
		useAddSongsToMyTeamMutation();
	const { teamSongs, dataUpdatedAt, refetch } = useTeamSongs();
	const { appendMilestone, hasMilestones, user } = useAuth();
	const { enqueueSnackbar } = useSnackbar();

	const { onlySongIds, excludeMyTeamSongs, hideActionButton, hideSelection } =
		config || {};

	const fireAddCustomSongEvent = useAnalyticsEvent(
		'Add_Song_Navigate_Add_Custom_Song',
	);
	const fireViewSongEvent = useAnalyticsEvent('Add_Song_Open_View_Song');
	const fireAddSongsEvent = useAnalyticsEvent('Add_Song_Add_Songs');
	const fireEnterSearch = useAnalyticsEvent('Add_Song_Enter_Search');

	const [searchTerm, setSearchTerm] = useState<string>(
		INITIAL_SONGS_LIST.searchTerm,
	);
	const [searchTagsStringified, setSearchTagsStringified] = useState(
		INITIAL_SONGS_LIST.searchTagsStringified,
	);
	const [clearFilters, setClearFilters] = useState(0);
	const [selected, setSelected] = useState<string[]>([]);
	const [searchFired, setSearchFired] = useState(false);
	const [songsList, setSongsList] = useState<SongsList>(INITIAL_SONGS_LIST);
	const [page, setPage] = useState<number>(0);
	const [loadPreviousRef, setLoadPreviousRef] =
		useState<HTMLDivElement | null>(null);

	const handleClose = useCallback(() => {
		onClose([]);
		setSelected([]);
		setSearchTerm(INITIAL_SONGS_LIST.searchTerm);
		setSearchTagsStringified(INITIAL_SONGS_LIST.searchTagsStringified);
		setSongsList(INITIAL_SONGS_LIST);
		setPage(0);
	}, [onClose]);

	const handleFilterChange: SearchAndFilterProps['onChange'] = useCallback(
		({ searchTerm: changedSearchTerm, selectedTags }) => {
			setSearchTerm(changedSearchTerm);
			setSearchTagsStringified(
				JSON.stringify(convertFilterEnumToSongKey(selectedTags)),
			);

			if (!searchFired) {
				fireEnterSearch();
				setSearchFired(true);
			}
		},
		[fireEnterSearch, searchFired],
	);

	const handleCardSelect = useCallback(
		(songId: string) => {
			if (selected.includes(songId)) {
				setSelected(selected.filter((id) => id !== songId));
			} else {
				setSelected([...selected, songId]);
			}
		},
		[selected],
	);

	const handleSubmit = useCallback(async () => {
		try {
			await addSongsToMyTeam({ songIds: selected });
			enqueueSnackbar('Songs added to the songlist', {
				variant: 'success',
			});
			fireAddSongsEvent({
				songIds: selected,
				length: selected.length,
			});
			refetch();
			onClose(selected);
			setSelected([]);
			setSearchTerm(INITIAL_SONGS_LIST.searchTerm);
			setSearchTagsStringified(INITIAL_SONGS_LIST.searchTagsStringified);
		} catch (e) {
			console.error(e);
			enqueueSnackbar(
				'Something went wrong while updating the songlist',
				{
					variant: 'error',
				},
			);
			return;
		}
	}, [
		addSongsToMyTeam,
		selected,
		enqueueSnackbar,
		fireAddSongsEvent,
		refetch,
		onClose,
	]);

	useEffect(() => {
		async function setSongs() {
			const selectedTags = searchTagsStringified
				? (JSON.parse(searchTagsStringified) as TagsByCategoryFilter)
				: null;
			// this is to check if the search query term that we've been working with to add new items to the result array is still the same
			// if the search query has changed we need to start from the beginning and start with a new result array
			if (
				songsList.searchTerm === searchTerm &&
				songsList.searchTagsStringified === searchTagsStringified &&
				page
			) {
				const searchResults = await searchSongs({
					filters: {
						term: searchTerm,
						tagsByCategory: selectedTags,
						onlySongIds: onlySongIds || null,
					},
					input: {
						limit: INITIAL_SONGLIST_RESULT_SIZE,
						offset: page * INITIAL_SONGLIST_RESULT_SIZE,
					},
					excludeMyTeamSongs,
				});

				const newSongsList = [
					...songsList.results,
					...(searchResults?.searchSongs?.items ?? []),
				].reduce<Song[]>((acc, song) => {
					if (
						!acc.find(({ id }) => song.id === id)
						// && !team.teamSongIds.includes(song.id)
						// && !teamSongs.find((ts) => ts.originSongId === song.id)
					) {
						return [...acc, song];
					}
					return acc;
				}, []);

				setSongsList({
					searchTerm,
					searchTagsStringified,
					results: newSongsList,
					totalCount: searchResults.searchSongs.totalCount,
				});
			} else {
				setPage(0);
				const searchResults = await searchSongs({
					filters: {
						term: searchTerm,
						tagsByCategory: selectedTags,
						onlySongIds: onlySongIds || null,
					},
					input: {
						limit: INITIAL_SONGLIST_RESULT_SIZE,
						offset: 0,
					},
					excludeMyTeamSongs,
				});

				setSongsList({
					searchTerm,
					searchTagsStringified,
					results: searchResults?.searchSongs?.items ?? [],
					totalCount: searchResults.searchSongs.totalCount,
				});
			}
		}
		setSongs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		searchTerm,
		searchTagsStringified,
		searchSongs,
		teamSongs,
		dataUpdatedAt,
		page,
		songsList.searchTerm,
	]);

	const handleLoadMoreVisible = useCallback(() => {
		if (!searchSongsLoading) {
			setPage((page) => page + 1);
		}
	}, [searchSongsLoading]);

	useEffect(() => {
		let observer: IntersectionObserver | undefined;

		if (loadPreviousRef !== null) {
			observer = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting) {
					handleLoadMoreVisible();
				}
			});

			observer.observe(loadPreviousRef);
		}
		return () => observer?.disconnect();
	}, [loadPreviousRef, handleLoadMoreVisible]);

	if (error) {
		console.error(error);
		enqueueSnackbar('Error while loading songs', { variant: 'error' });
	}

	return (
		<SonglistPanelLayout
			open={true}
			isLoading={searchSongsLoading}
			handleClose={handleClose}
			handleFilterChange={handleFilterChange}
			clearFilters={clearFilters}
			title="Add from library"
			excludeFilters={[
				FilterEnum.ROTATION_STATUS,
				FilterEnum.DEFAULT_KEY,
			]}
			hideViewBy
			actionButton={
				hideActionButton || selected.length === 0 ? undefined : (
					<AsaphButton
						color="blue"
						label={`Add ${selected.length} song${
							selected.length > 1 ? 's' : ''
						}`}
						onClick={handleSubmit}
						fullWidth
					/>
				)
			}
			listClassName={
				songsList.totalCount === 0 ? styles['list-full-height'] : ''
			}
		>
			{songsList.results.map((song) => {
				const checked = selected.includes(song.id);
				const isAlreadyAdded = teamSongs.filter(
					(ts) => ts.originSongId === song.id,
				).length;

				return (
					<SongCard
						key={song.id}
						song={song}
						onClick={() => {
							openSong({ songId: song.id });
							fireViewSongEvent({
								id: song.id,
								artist: song.artist,
								title: song.name,
							});
						}}
						onSelect={() => handleCardSelect(song.id)}
						selectable={!hideSelection && !isAlreadyAdded}
						displayKey={false}
						checked={!hideSelection && checked}
						negativeSpacing
						trailingComponent={
							isAlreadyAdded ? (
								<div className={styles['added-label']}>
									Added
								</div>
							) : null
						}
					/>
				);
			})}
			{songsList.totalCount > songsList.results.length && (
				<div
					ref={(newRef) => setLoadPreviousRef(newRef)}
					className={styles['loading']}
				>
					<CircularProgress />
				</div>
			)}
			{!searchSongsLoading && !songsList.totalCount && (
				<EmptyResourceMessage
					onActionClick={() => setClearFilters(clearFilters + 1)}
					title="No songs"
					subtitle="Your filters have returned 0 results"
					actionLabel="Clear filters"
				/>
			)}
			{!hideActionButton &&
				!!songsList.totalCount &&
				songsList.totalCount === songsList.results.length &&
				!selected.length && (
					<div className={styles['request-a-song']}>
						<div className={styles['heading']}>
							Can't find the song you're looking for?
						</div>
						<AsaphButton
							color="blue"
							label="Create new song"
							onClick={() => {
								handleClose();
								onCustomSongOpen();
								fireAddCustomSongEvent();
							}}
							fullWidth
						/>
					</div>
				)}
		</SonglistPanelLayout>
	);
};
